import React from "react";
import "./Resume.css";

const coreCompetencies = [
  "Digital Product and Platform Management",
  "Agile and Scrum Project Management",
  "User Experience (UX, UI) and Service Design",
  "Cloud Solutions and Deployment",
  "Design Thinking and Rapid Prototyping",
  "Strategic Business Analysis and Data Insights",
  "Process Workflows in Industry 4.0 and Supply Chain",
];

const technicalSkills = [
  "Agile Product Management: Certified Scrum Master (CSM), SAFe Agilist",
  "Full Stack Software Engineering: ReactJS, JavaScript, HTML, CSS, ExpressJS, NodeJS, MongoDB, Python, Django",
  "Cloud Solutions: AWS, Azure, Google Cloud",
  "Data Analysis Tools: Power BI, Tableau, SQL, R",
  "Prototyping Tools: Figma, Sketch, Axure, InVision, Replit, Google AI Studio, v0 by Vercel, Figma Make",
  "Project Management Tools: Jira, Notion, Trello, Asana",
  "Microsoft Office Suite",
];

const keyAchievements = [
  "Successfully launched the Eat Drink Shop Healthy campaign, deploying a digital system that supports over 1.2 million users and 20,000 partner touchpoints.",
  "Spearheaded the rapid go-live of Eunoia's Go Digital Platform, integrating with multiple third-party systems and supporting prominent brands in the F&B sector.",
  "Contributed to the Central Ops planning and execution of the Youth Olympic Games 2010 Catering Meals Ordering System, ensuring efficient service delivery for over 7,000 athletes, officials, and staff across multiple venues.",
];

const keyAchievementLinks = [
  {
    label: "Eat Drink Shop Healthy",
    href: "https://www.healthhub.sg/programmes/nutrition-hub/eat-drink-shop-healthy-challenge?utm_source=google&utm_medium=paid-search&utm_campaign=fy24-nl-edsh-ao&utm_content=fy24-nl-edsh-ao_google_paid-search_edsh-hcs&gad_source=1#home",
  },
  {
    label: "Eunoia Platform",
    href: "https://www.eunoia.asia/platform",
  },
  {
    label: "SATS Youth Olympic Games",
    href: "https://www.sats.com.sg/content/dam/sats/corporate/documents/investors/annual-reports/sats_fy1011_ceo_statement.pdf.downloadasset.pdf#page=3",
  },
];

const recentProjectGroups = [
  {
    heading: "Startup Hipster Pte Ltd",
    subheading: "IMDA TIPP - Rapid Prototyping",
    items: [
      {
        title: "Learning Management System",
        detail:
          "A front-end prototype showcasing an AI-integrated engine that enables interest-driven, self-paced learning experiences.",
        link: "https://alex-ba.vercel.app/",
      },
      {
        title: "Career Advisory Web Platform",
        detail:
          "An AI-powered platform to guide job seekers through personalized career exploration, offering tailored recommendations and actionable insights through Mock Interviews etc.",
        link: "https://career-grit-amalinazafirahh.replit.app/",
      },
      {
        title: "Public Announcement System",
        detail:
          "A Text-to-Speech platform for automated AI-generated announcements and broadcasting.",
        link: "https://voice-blast-amalinazafirahh.replit.app/",
      },
      {
        title: "Event Trail Challenge",
        detail:
          "A geo-location event app with an interactive trail map, checkpoint pins, and social sharing to drive engagement and event visibility.",
        link: "https://lbtl-amalinazafirahh.replit.app/",
      },
    ],
  },
  {
    heading: "General Assembly Course",
    subheading: "IMDA TIPP - Full Stack Software Development",
    items: [
      {
        title: "Geopayment System",
        detail:
          "A geofencing payment system designed for use at event spaces, carparks, gyms etc. Technologies used: Python, Django, ReactJS, Google APIs, Stripe APIs, Google Cloud Solutions.",
        link: "https://rfid-project-424315.web.app/",
      },
      {
        title: "eCommerce Platform",
        detail:
          "End-to-End online shopping platform with Products management, Cart, Favourites and Payment features. Technologies used: Javascript, ReactJS, Express, MongoDB, Stripe APIs, Vercel and Render.",
        link: "https://webstore-rosy.vercel.app",
      },
      {
        title: "AI Fruit Journal",
        detail:
          "A mobile camera AI integrated image detection fruit journal. Technologies used: ReactJS, Google Maps APIs, Clarifai, Airtable, Cloudinary and Vercel.",
        link: "https://fruit-journal-clarifai-488v.vercel.app",
      },
    ],
  },
];

const professionalExperience = [
  {
    role: "Digital Technology & Transformation Project Manager",
    company: "Singapore Food Agency (SFA)",
    period: "Sep 2025 - Present",
    points: [
      "Drive IT governance and end-to-end project management within a government setting, ensuring compliance with IT architecture standards, security frameworks, and organizational roadmaps.",
      "Support key national programs, analytics platforms, backend systems, and AI/IoT initiatives to enhance the national food safety ecosystem and digital capabilities.",
      "Leverage product management expertise to champion user-centered design, translating stakeholder requirements into scalable technical solutions.",
      "Role-model collaborative teamwork and utilized modern productivity and project management tools to streamline delivery timelines, manage risks, and elevate service quality.",
    ],
  },
  {
    role: "Digital Project Manager (IMDA TECH IMMERSION & PLACEMENT PROGRAMME)",
    company: "Hipster Pte Ltd",
    period: "Feb 2025 - Aug 2025",
    points: [
      "Led end-to-end delivery of digital products across Government and Commercial sectors, managing full project lifecycles from user research to launch and post-deployment support.",
      "Architected key platforms, including a Home Spa app suite (with intelligent booking, payments, and promotions), an Events Management system, and a Gov Agency Mapping app with AR and navigation features.",
      "Applied Design Thinking and CX research to ensure user-centric outcomes, improving product effectiveness and stakeholder satisfaction.",
      "Drove cross-functional collaboration to prototype and deliver scalable solutions rapidly, accelerating innovation and development cycles.",
    ],
  },
  {
    role: "Manager, Healthy Food & Dining (Planning & Strategy Team)",
    company: "Health Promotion Board",
    period: "June 2019 - September 2023",
    points: [
      "Led digital transformation projects that improved organizational productivity and enhanced healthier food purchases, including developing roadmaps and tech solutions within the Healthy Dining ecosystem.",
      "Owned the Healthy 365 Eat Drink Shop Healthy POS Solution across v1 and v2, aligning user needs, stakeholder priorities, product roadmap, and delivery milestones.",
      "Managed product backlog, requirements clarification, progress tracking, and system integration workflows for POS and eCommerce purchase data systems.",
      "Partnered cross-functionally on POS partner onboarding, deployment, maintenance, issue resolution, and user feedback loops across over 20,000 F&B touchpoints.",
      "Co-led annual department work-planning and developed corporate development strategies and reports, including 5-year work plans and budget management.",
    ],
  },
  {
    role: "Product Manager",
    company: "Eunoia Pte Ltd",
    period: "August 2017 - June 2019",
    points: [
      "Directed the Go Digital initiative, achieving rapid deployment of ordering web applications for F&B owners, including integration with third-party systems (Payments, POS, CRM, ERP) within a 1-day go-live timeframe.",
      "Developed and executed product and Go-to-Market strategies and roadmaps, aligning with market trends and organizational objectives to drive growth and competitive advantage.",
      "Led cross-functional teams in the design and development of digital platforms, enhancing user experience and operational efficiency through continuous testing and iteration.",
      "Transformed user feedback into actionable product enhancements, fostering a culture of innovation and responsiveness to market demands.",
    ],
  },
  {
    role: "Business Designer",
    company: "Eunoia Pte Ltd",
    period: "Aug 2015 - Aug 2017",
    points: [
      "Led technology innovation projects, creating bespoke digital solutions that streamlined processes and improved service delivery in the F&B sector.",
      "Applied Design Thinking methodologies to develop user-centric products, managing the end-to-end development and deployment of custom applications for clients such as Alt. Pizza, Artisan Boulangerie Co. and The Soup Spoon.",
      "Facilitated knowledge-sharing initiatives, fostering a culture of continuous learning and innovation within the team.",
    ],
  },
  {
    role: "Project Executive",
    company: "Foodservice Consultants Singapore Pte Ltd",
    period: "Jan 2013 - Mar 2015",
    points: [
      "Led the design and iterative testing of over 20 kitchen workflows, ensuring optimal efficiency and alignment with the evolving needs of large-scale international hospitality projects such as the Westin and Sheraton properties.",
      "Designed and implemented F&B concepts for high-profile projects, utilizing agile methodologies to adapt swiftly to market changes and enhance supply chain and food safety processes.",
      "Managed end-to-end project execution, from commercial kitchen design to financial administration, ensuring seamless integration and support across supply chain functions.",
    ],
  },
  {
    role: "Business Development Executive",
    company: "Sats Pte Ltd",
    period: "May 2010 - Sep 2012",
    points: [
      "Led export Sales & Marketing efforts for industrial catering and special diets across diverse markets, driving sustainable growth and market expansion.",
      "Spearheaded new product development for ready-to-eat meals, focusing on logistics, warehousing, and last-mile delivery workflows.",
      "Managed key accounts, including military clientele, overseeing large-scale catering projects and ensuring compliance with international quality standards.",
    ],
  },
];

const education = [
  {
    program: "Software Engineering (IMDA TIPP)",
    institution: "General Assembly",
    date: "June 2024",
  },
  {
    program: "MEng, Innovation by Design",
    institution: "Singapore University of Technology & Design, Singapore",
    date: "December 2021",
  },
  {
    program: "Specialisation in Digital Manufacturing Design & Technology",
    institution: "University at Buffalo, Coursera",
    date: "December 2019",
  },
  {
    program: "Specialist Diploma in Design Strategy & Management (Innovation)",
    institution: "Nanyang Polytechnic, Singapore",
    date: "December 2018",
  },
  {
    program: "BSc Food Science",
    institution: "University of Otago, New Zealand",
    date: "August 2006",
  },
];

const certifications = [
  "NUS Digital Product Management",
  "Certified Scrum Master (CSM)",
  "Leading SAFe Agilist 6.0",
  "Figma UI Design",
  "Google Analytics Professional Certificate",
  "Data Analytics with Excel",
  "Power BI",
];

const Resume = () => {
  const handleDownloadPdf = () => {
    window.print();
  };

  return (
    <div className="rv2-page">
      <section className="rv2-wrap">
        <header className="rv2-hero">
          <div>
            <p className="rv2-kicker">Resume</p>
            <h1 className="rv2-name">
              Amalina Hashim | Digital Experience and Innovation Strategist
            </h1>
            <p className="rv2-title">
              email: amalinazafirahhashim@gmail.com |{" "}
              <a
                href="https://amalinahashim.com"
                target="_blank"
                rel="noreferrer"
              >
                personal website
              </a>{" "}
              |{" "}
              <a
                href="https://sg.linkedin.com/in/amalina-hashim-75644bb7"
                target="_blank"
                rel="noreferrer"
              >
                linkedin
              </a>{" "}
              |{" "}
              <a
                href="https://github.com/Amalina-Hashim"
                target="_blank"
                rel="noreferrer"
              >
                github
              </a>
            </p>
          </div>

          <button
            className="rv2-download"
            type="button"
            onClick={handleDownloadPdf}
          >
            Download PDF
          </button>
        </header>

        <div className="rv2-grid">
          <main className="rv2-main">
            <section className="rv2-card rv2-card-pink">
              <h2>CAREER PROFILE</h2>
              <p className="rv2-quote">
                "Outcome and results-driven innovation manager with over 10 years
                of experience leading cross-functional teams to develop and
                launch high-impact digital products and platforms. Proven
                expertise in digital transformation, agile project management,
                and user-centric design, with a strong track record of
                delivering solutions that enhance customer experience and drive
                business growth."
              </p>
            </section>

            <section className="rv2-card rv2-card-pink">
              <h2>CORE COMPETENCIES</h2>
              <div className="rv2-tags rv2-tags-core">
                {coreCompetencies.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>

            <section className="rv2-card rv2-card-purple">
              <h2>TECHNICAL SKILLS</h2>
              <div className="rv2-tags rv2-tags-tech">
                {technicalSkills.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>

            <section className="rv2-card rv2-card-pink">
              <h2>KEY ACHIEVEMENTS</h2>
              <ul className="rv2-key-achievements">
                {keyAchievements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="rv2-links">
                {keyAchievementLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </section>

            <section className="rv2-card rv2-card-pink">
              <h2>EDUCATION</h2>
              <div className="rv2-education-list">
                {education.map((item) => (
                  <article key={item.program} className="rv2-education-item">
                    <h3>{item.program}</h3>
                    <p>{item.institution}</p>
                    <span>{item.date}</span>
                  </article>
                ))}
              </div>
            </section>

            <section className="rv2-card rv2-card-purple">
              <h2>RELATED PROFESSIONAL EXPERIENCE</h2>
              <div className="rv2-timeline">
                {professionalExperience.map((item) => (
                  <article
                    key={`${item.period}-${item.role}`}
                    className="rv2-item"
                  >
                    <p className="rv2-period">{item.period}</p>
                    <h3>{item.role}</h3>
                    <p className="rv2-company">{item.company}</p>
                    <ul className="rv2-exp-list">
                      {item.points.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </section>
          </main>

          <aside className="rv2-side">
            <section className="rv2-card rv2-card-purple rv2-card-compact">
              <h2>CERTIFICATIONS</h2>
              <div className="rv2-tags rv2-tags-cert">
                {certifications.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>

            <section className="rv2-card rv2-card-purple rv2-card-compact">
              <h2>RECENT SOFTWARE ENGINEERING PROJECTS</h2>
              {recentProjectGroups.map((group) => (
                <div key={group.heading} className="rv2-project-group">
                  <h3 className="rv2-project-heading">{group.heading}</h3>
                  <p className="rv2-project-subheading">{group.subheading}</p>
                  <ul className="rv2-project-list">
                    {group.items.map((item) => (
                      <li key={item.title}>
                        <a href={item.link} target="_blank" rel="noreferrer">
                          {item.title}
                        </a>
                        <p>{item.detail}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Resume;
